import React from 'react';
import styled from 'styled-components';
import { Box, Switch, Text, Colors } from '@fozito/common-ui';
import { Form } from 'antd';
import FormItemValue from 'components/FormItemValue';
import { formatCurrency } from 'utils/formatCurrency';
import { IUICashierFormValues } from '../cashier.interface';
import shopService from 'modules/shop/services';

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  background: #E5E6EB;
`;

const Content = styled.div`
  background: #E5E6EB;
  display: flex;
  flex-direction: column;
  padding: 10px;
  padding-bottom: 4px;
`;
const Row = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const BalanceRow = styled(Row)`
  justify-content: flex-end;
`;

const Divider = styled.div`
  height: 1px;
  background: #232F3E;
  margin: 0px 0;
  margin-bottom: 4px;
`;

const InfoGroup = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  &.between {
    align-self: stretch;
    space-between;
    .title_h6_20_regular {
      flex: 1;
      text-align: left;
    }
  }
`;

const Label = styled(Text)`
  color: ${Colors.neutral.text[5]};
  font-size: 14px;
  line-height: 24px;
  font-weight: 600;
  text-align: left;
`;

const Value = styled(Text)`
  color: ${Colors.neutral.text[5]};
  font-size: 14px;
  font-weight: 600;
`;

const TotalValue = styled(Text)`
  color: ${Colors.primary[6]};
  font-size: 18px;
  font-weight: 600;
`;

const SwitchContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #FFE5E7;
  border-radius: 4px;
`;

const renderInfo = (value?: number) => <Value title_h6_20_regular style={{ textAlign: 'right', fontSize: 16 }}>{formatCurrency((value || 0) > 0 ? value : 0)}</Value>;
const renderInfoBold = (value?: number) => <Value title_h6_20_regular style={{ textAlign: 'right', fontWeight: '600' }}>{formatCurrency((value || 0) > 0 ? value : 0)}</Value>;

const TicketFooter = () => {
  const cashIncentiveConfig = shopService.selectors.cashIncentive();
  const feeCreditCardConfig = shopService.selectors.feeCreditCard();
  return (
    <Container>
      <Content>
        <BalanceRow>
          <InfoGroup>
            <Label title_h6_20_regular style={{ fontWeight: '600', fontSize: 20 }}>Balance:</Label>
            <FormItemValue name='balance' render={renderInfoBold} />
          </InfoGroup>
        </BalanceRow>
        <Row>
          <Box flex={1} display='flex' flexDirection='column'>
            <InfoGroup className='between'>
              <Text title_h6_20_regular style={{ fontSize: 16 }}>Disc. By Item:</Text>
              <FormItemValue name='discountByItem' render={renderInfo} />
            </InfoGroup>
            <InfoGroup className='between'>
              <Text title_h6_20_regular style={{ fontSize: 16 }}>Disc. By Ticket:</Text>
              <FormItemValue name='discountByTicket' render={renderInfo} />
            </InfoGroup>
          </Box>
          <Box flex={1} display='flex' flexDirection='column'>
            <InfoGroup className='between'>
              <Text title_h6_20_regular style={{ fontSize: 16 }}>Sale Tax:</Text>
              <FormItemValue name='saleTax' render={renderInfo} />
            </InfoGroup>
            <InfoGroup className='between'>
              <Text title_h6_20_regular style={{ fontSize: 16 }}>Used Tax:</Text>
              <FormItemValue name='useTax' render={renderInfo} />
            </InfoGroup>
          </Box>
        </Row>
        <Divider style={{ marginTop: 8 }} />
        <Row>
          <Box flex={1} display='flex' flexDirection='column'>
            <InfoGroup className='between'>
              <Text title_h6_20_regular style={{ fontWeight: '600' }}>Subtotal:</Text>
              <FormItemValue name='subTotalBalance' render={renderInfoBold} />
            </InfoGroup>
          </Box>
          <Box flex={1} display='flex' flexDirection='column'>
            <InfoGroup className='between'>
              <Text title_h6_20_regular style={{ fontSize: 24, fontWeight: '600' }}>Total:</Text>
              <FormItemValue
                name='total'
                render={(value) => (
                  <TotalValue title_h7_20_semibold style={{ fontSize: 28 }}>{formatCurrency(value > 0 ? value : 0)}</TotalValue>
                )}
              />
            </InfoGroup>
          </Box>
        </Row>
      </Content>
      <SwitchContainer>
        <Row>
          {!!feeCreditCardConfig?.enable && <Form.Item
            shouldUpdate
            noStyle
            dependencies={['feeCreditCard', 'feeCreditCardAmount']}
          >
            {({ getFieldsValue }) => {
              const { feeCreditCard, feeCreditCardAmount } =
                getFieldsValue() as IUICashierFormValues;
              const label =
                feeCreditCard?.feeType === 'PERCENT'
                  ? `Card Fee: ${feeCreditCard?.feeValue}% (${formatCurrency(
                    feeCreditCardAmount
                  )})`
                  : `Card Fee: ${formatCurrency(feeCreditCardAmount)}`;
              return (
                <Form.Item name='chargeFee' noStyle>
                  <SwitchLabel label={label} />
                </Form.Item>
              );
            }}
          </Form.Item>}

          {!!cashIncentiveConfig?.enable && <Form.Item
            shouldUpdate
            noStyle
            dependencies={['cashIncentive', 'cashIncentiveAmount', 'cashIncentiveDiscount']}
          >
            {({ getFieldsValue }) => {
              const { cashIncentive, cashIncentiveAmount } =
                getFieldsValue() as IUICashierFormValues;
              const label =
                cashIncentive?.discountType === 'PERCENT'
                  ? `Cash/Check Incentive: ${cashIncentive?.discountValue
                  }% (${formatCurrency(cashIncentiveAmount)})`
                  : `Cash/Check Incentive: ${formatCurrency(
                    cashIncentiveAmount
                  )}`;
              return (
                <Form.Item name='cashIncentiveDiscount' noStyle>
                  <SwitchLabel label={label} />
                </Form.Item>
              );
            }}
          </Form.Item>}
        </Row>
      </SwitchContainer>
    </Container>
  );
};

const StyledSwitch = styled(Switch)`
  margin-right: 8px;
`;

const SwitchLabel = ({
  onChange,
  label,
  value,
}: {
  label?: string;
  value?: boolean;
  onChange?: (value: boolean) => void;
}) => {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }} >
      <StyledSwitch value={value} onChange={onChange} />
      <Label title_h9_18_semibold >{label}</Label>
    </label>
  );
};

export default TicketFooter;
